import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Mail } from 'lucide-react';
import { FaLinkedin, FaGithub } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { useScrollSpy } from '../../hooks/useScrollSpy';
import logoWhite from '../../assets/img/logo-white.png';
import logoBlack from '../../assets/img/logo-black.png';
import '../../styles/navbar.css';

const NAV_LINKS = [
  { id: 'home',     label: 'Home' },
  { id: 'skills',   label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'ecerts',   label: 'E-Certificates' },
];

const SECTION_IDS = NAV_LINKS.map(l => l.id);

const SOCIALS = [
  { href: 'https://www.linkedin.com/in/ervin-tejada-a18239242', icon: FaLinkedin, label: 'LinkedIn' },
  { href: 'https://github.com/Nevriii',                          icon: FaGithub,   label: 'GitHub' },
  { href: 'mailto:ervintejada05@gmail.com',                     icon: Mail,       label: 'Email' },
];

const drawerVariants = {
  hidden:  { opacity: 0, y: -8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.22, ease: 'easeOut' } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.18, ease: 'easeIn' } },
};

export function NavBar() {
  const { isDark, toggleTheme } = useTheme();
  const activeId = useScrollSpy(SECTION_IDS, 80);
  const [scrolled, setScrolled]     = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Determine which logo to use based on theme
  const logo = isDark ? logoWhite : logoBlack;

  // Track scroll for frosted glass appearance
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close drawer on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 768) setDrawerOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  // Trap focus + close on Escape for drawer
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    if (drawerOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const scrollTo = (id) => {
    setDrawerOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleThemeToggle = (e) => {
    const x = e.clientX;
    const y = e.clientY;

    if (!document.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      toggleTheme();
      return;
    }

    const transition = document.startViewTransition(() => {
      toggleTheme();
    });

    transition.ready.then(() => {
      const radius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      );

      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${radius}px at ${x}px ${y}px)`
          ],
        },
        {
          duration: 500,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      );
    });
  };

  return (
    <>
      <header className={`navbar${scrolled ? ' scrolled' : ''}`} role="banner">
        <div className="nav-container">
          {/* Logo */}
          <a href="#home" className="nav-logo" onClick={() => scrollTo('home')} aria-label="Go to home">
            <img src={logo} alt="Vin Tejada logo" />
          </a>

          {/* Desktop nav links */}
          <nav aria-label="Main navigation">
            <ul className="nav-links">
              {NAV_LINKS.map(({ id, label }) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className={activeId === id ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); scrollTo(id); }}
                    aria-current={activeId === id ? 'page' : undefined}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Right actions */}
          <div className="nav-actions">
            {/* Social icons — desktop only */}
            <div className="nav-socials" aria-label="Social links">
              {SOCIALS.map(({ href, icon: Icon, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                  <Icon size={18} strokeWidth={1.8} />
                </a>
              ))}
            </div>

            {/* Theme toggle */}
            <button
              className="theme-toggle-btn"
              onClick={handleThemeToggle}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark
                ? <Sun size={18} strokeWidth={1.8} />
                : <Moon size={18} strokeWidth={1.8} />
              }
            </button>

            {/* Mobile hamburger */}
            <button
              className={`hamburger-btn${drawerOpen ? ' open' : ''}`}
              onClick={() => setDrawerOpen(prev => !prev)}
              aria-expanded={drawerOpen}
              aria-controls="mobile-nav"
              aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            >
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <motion.nav
            id="mobile-nav"
            className="mobile-drawer"
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            aria-label="Mobile navigation"
          >
            <div className="mobile-drawer-inner">
              <ul className="mobile-nav-links">
                {NAV_LINKS.map(({ id, label }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className={activeId === id ? 'active' : ''}
                      onClick={(e) => { e.preventDefault(); scrollTo(id); }}
                      aria-current={activeId === id ? 'page' : undefined}
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mobile-drawer-footer">
                <div className="mobile-socials">
                  {SOCIALS.map(({ href, icon: Icon, label }) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                      <Icon size={20} strokeWidth={1.8} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}
